"use client";

import { c as _c } from "react/compiler-runtime";
import { LazyMotion, domMax, m } from "framer-motion";
import Image from "next/image";
import { usePathname, useSearchParams } from "next/navigation";
import { useEffect } from "react";
import { useTitle } from "@/contexts/TitleContext";
import { useUnreadIndicator } from "@/hooks/useUnreadIndicator";
import { useUnreadNotifications } from "@/hooks/useUnreadNotifications";
import { useAuth } from "../auth/Auth";
import { useSeizeConnectContext } from "../auth/SeizeConnectContext";
import { useNotificationsContext } from "../notifications/NotificationsContext";
import { getActiveWaveIdFromUrl } from "@/helpers/navigation.helpers";
import { isNavItemActive } from "./isNavItemActive";
import { getActiveViewFromUrl, useViewContext } from "./ViewContext";
import type { NavItem as NavItemData } from "./navTypes";
interface Props {
  readonly item: NavItemData;
  readonly isCurrentWaveDm?: boolean;
}
const iconSlotClass = "tw-mt-4 tw-flex tw-h-9 tw-items-center tw-justify-center";
const ActiveNavIndicator = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <LazyMotion features={domMax}><m.div layoutId="nav-indicator" className="tw-absolute tw-left-0 tw-top-0 tw-h-0.5 tw-w-full tw-rounded-full tw-bg-white" /></LazyMotion>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const NavItemContent = t0 => {
  const $ = _c(57);
  const {
    item,
    isCurrentWaveDm: t1
  } = t0;
  const isCurrentWaveDm = t1 === undefined ? false : t1;
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const activeWaveId = getActiveWaveIdFromUrl({
    pathname,
    searchParams
  });
  const activeView = getActiveViewFromUrl({
    activeWaveId,
    searchParams
  });
  const {
    handleNavClick
  } = useViewContext();
  const {
    name
  } = item;
  const {
    icon
  } = item;
  const {
    address,
    seizeConnect
  } = useSeizeConnectContext();
  const {
    connectedProfile
  } = useAuth();
  let t2;
  if ($[0] !== connectedProfile?.handle || $[1] !== connectedProfile?.normalised_handle) {
    t2 = (connectedProfile?.normalised_handle ?? connectedProfile?.handle)?.toLowerCase();
    $[0] = connectedProfile?.handle;
    $[1] = connectedProfile?.normalised_handle;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const normalizedConnectedHandle = t2;
  let t3;
  if ($[3] !== address) {
    t3 = address?.toLowerCase();
    $[3] = address;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const normalizedConnectedAddress = t3;
  const profileSlug = normalizedConnectedHandle ?? normalizedConnectedAddress;
  const profileHref = profileSlug ? `/${profileSlug}` : null;
  const {
    setTitle
  } = useTitle();
  const {
    notifications,
    haveUnreadNotifications
  } = useUnreadNotifications(item.name === "Notifications" ? connectedProfile?.handle ?? null : null);
  const t4 = item.name === "Messages" ? connectedProfile?.handle ?? null : null;
  let t5;
  if ($[5] !== t4) {
    t5 = {
      type: "messages",
      handle: t4
    };
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const {
    hasUnread: hasUnreadMessages
  } = useUnreadIndicator(t5);
  const {
    removeAllDeliveredNotifications
  } = useNotificationsContext();
  let t6;
  if ($[7] !== haveUnreadNotifications || $[8] !== item.name || $[9] !== notifications?.unread_count || $[10] !== removeAllDeliveredNotifications || $[11] !== setTitle) {
    t6 = () => {
      if (item.name !== "Notifications") {
        return;
      }
      if (haveUnreadNotifications) {
        const unreadNotificationsCount = notifications?.unread_count ?? 0;
        setTitle(`(${unreadNotificationsCount}) Notifications | 6529.io`);
      }
      if (!haveUnreadNotifications) {
        removeAllDeliveredNotifications();
        setTitle("Notifications | 6529.io");
      }
    };
    $[7] = haveUnreadNotifications;
    $[8] = item.name;
    $[9] = notifications?.unread_count;
    $[10] = removeAllDeliveredNotifications;
    $[11] = setTitle;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const t7 = notifications?.unread_count;
  let t8;
  if ($[13] !== haveUnreadNotifications || $[14] !== item.name || $[15] !== removeAllDeliveredNotifications || $[16] !== setTitle || $[17] !== t7) {
    t8 = [haveUnreadNotifications, item.name, t7, removeAllDeliveredNotifications, setTitle];
    $[13] = haveUnreadNotifications;
    $[14] = item.name;
    $[15] = removeAllDeliveredNotifications;
    $[16] = setTitle;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  useEffect(t6, t8);
  if (item.disabled) {
    let t9;
    if ($[19] !== icon || $[20] !== item.iconComponent || $[21] !== item.iconSizeClass || $[22] !== name) {
      t9 = <div className={iconSlotClass}>{item.iconComponent ? <item.iconComponent className={`${item.iconSizeClass ?? "tw-size-7"} tw-text-iron-500`} /> : <Image src={icon} alt={name} width={24} height={24} unoptimized={true} className={item.iconSizeClass ?? "tw-size-7"} />}</div>;
      $[19] = icon;
      $[20] = item.iconComponent;
      $[21] = item.iconSizeClass;
      $[22] = name;
      $[23] = t9;
    } else {
      t9 = $[23];
    }
    let t10;
    if ($[24] !== name || $[25] !== t9) {
      t10 = <button type="button" aria-label={name} aria-disabled="true" disabled={true} className="tw-pointer-events-none tw-relative tw-flex tw-h-full tw-w-full tw-min-w-0 tw-flex-col tw-items-center tw-justify-start tw-border-0 tw-bg-transparent tw-opacity-40 tw-transition-colors focus:tw-outline-none">{t9}</button>;
      $[24] = name;
      $[25] = t9;
      $[26] = t10;
    } else {
      t10 = $[26];
    }
    return t10;
  }
  const iconSizeClass = item.iconSizeClass ?? "tw-size-7";
  const isProfileItem = item.kind === "route" && item.name === "Profile";
  const normalizedPathname = pathname.toLowerCase();
  const isProfileActive = isProfileItem && activeView === null && profileHref !== null && (normalizedPathname === profileHref || normalizedPathname.startsWith(`${profileHref}/`));
  const isActive = isProfileItem ? isProfileActive : isNavItemActive(item, pathname, searchParams, activeView, isCurrentWaveDm);
  let t9;
  if ($[27] !== address || $[28] !== handleNavClick || $[29] !== item || $[30] !== profileHref || $[31] !== seizeConnect) {
    t9 = () => {
      if (item.kind === "route" && item.name === "Profile") {
        if (!address) {
          seizeConnect();
          return;
        }
        handleNavClick({
          ...item,
          href: profileHref ?? item.href
        });
        return;
      }
      handleNavClick(item);
    };
    $[27] = address;
    $[28] = handleNavClick;
    $[29] = item;
    $[30] = profileHref;
    $[31] = seizeConnect;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  const handleClick = t9;
  const t10 = isActive ? "page" : undefined;
  let t11;
  if ($[33] !== isActive) {
    t11 = isActive && <ActiveNavIndicator />;
    $[33] = isActive;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== icon || $[36] !== iconSizeClass || $[37] !== isActive || $[38] !== item.iconComponent || $[39] !== name) {
    t12 = item.iconComponent ? <item.iconComponent className={`${iconSizeClass} ${isActive ? "tw-text-white" : "tw-text-iron-500"}`} /> : <Image src={icon} alt={name} width={24} height={24} unoptimized={true} className={iconSizeClass} />;
    $[35] = icon;
    $[36] = iconSizeClass;
    $[37] = isActive;
    $[38] = item.iconComponent;
    $[39] = name;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  let t13;
  if ($[41] !== haveUnreadNotifications || $[42] !== item.name) {
    t13 = item.name === "Notifications" && haveUnreadNotifications && <div className="tw-absolute -tw-right-1 -tw-top-1 tw-h-2.5 tw-w-2.5 tw-rounded-full tw-bg-red" />;
    $[41] = haveUnreadNotifications;
    $[42] = item.name;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  let t14;
  if ($[44] !== hasUnreadMessages || $[45] !== item.name) {
    t14 = item.name === "Messages" && hasUnreadMessages && <div className="tw-absolute -tw-right-1 -tw-top-1 tw-h-2.5 tw-w-2.5 tw-rounded-full tw-bg-red" />;
    $[44] = hasUnreadMessages;
    $[45] = item.name;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  let t15;
  if ($[47] !== t12 || $[48] !== t13 || $[49] !== t14) {
    t15 = <div className={`tw-relative ${iconSlotClass}`}>{t12}{t13}{t14}</div>;
    $[47] = t12;
    $[48] = t13;
    $[49] = t14;
    $[50] = t15;
  } else {
    t15 = $[50];
  }
  let t16;
  if ($[51] !== handleClick || $[52] !== name || $[53] !== t10 || $[54] !== t11 || $[55] !== t15) {
    t16 = <button type="button" aria-label={name} aria-current={t10} onClick={handleClick} className="tw-relative tw-flex tw-h-full tw-w-full tw-min-w-0 tw-flex-col tw-items-center tw-justify-start tw-border-0 tw-bg-transparent tw-transition-colors focus:tw-outline-none">{t11}{t15}</button>;
    $[51] = handleClick;
    $[52] = name;
    $[53] = t10;
    $[54] = t11;
    $[55] = t15;
    $[56] = t16;
  } else {
    t16 = $[56];
  }
  return t16;
};
const NavItem = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <NavItemContent {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
export default NavItem;